// Neu nach /opt/lilasprojekte/frontend/src/components/app/Shell.tsx (Stand 2026-09-29) und
// SPEC 03 §7.2: Kopfleiste · Statusband · #main-scroll (<main class="blatt"> + Fuß) · Chat ·
// Reiterleiste · Übersichtsblatt · Tastenkürzel. Hier hängen auch die Dinge, die es genau
// EINMAL je Verfahren gibt: die Live-Verbindung (useLive), der Esc-Stapel und die globalen
// Tasten (F und Strg/⌘+K Befehlssuche, „/“ Fragebar, ? Tastenkürzel).
//
// Verfahren-Kontext (§7.1): Wege mit :V nehmen V aus dem Pfad; Objektwege (/d, /b, /m, /x,
// /a, /q, /r) aus dem Code-Präfix des Verweises, sonst setzt der Bildschirm V nach dem Laden
// (setzeVerfahren). Ein :V, das nicht in `ich.verfahren` steht, zeigt „Unbekannt“ — derselbe
// Satz wie die 404 der API.
import { Suspense, lazy, useEffect, useMemo, useState, type ComponentType, type CSSProperties } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLive } from "../../hooks/useLive";
import { useIch } from "../../hooks/useIch";
import { useTasten } from "../../hooks/useTasten";
import { retryOnStaleShell } from "../../lib/chunk";
import { hatRecht, verfahrenAusCode } from "../../lib/rechte";
import { escOberste, useStore } from "../../store/useStore";
import { Modal } from "../ui";
import { Befehlssuche, useBefehlssuche } from "./Befehlssuche";
import { Footer } from "./Footer";
import { MobilLeiste } from "./MobilLeiste";
import { einzigesVerfahren, fragebarFokussieren, fragebarSpaeterFokussieren } from "./navigation";
import { ScreenBoundary } from "./ScreenBoundary";
import { Statusband, useBand } from "./Statusband";
import { TopNav } from "./TopNav";
import { Tastenkuerzel } from "./Tastenkuerzel";
import { Unbekannt } from "../../screens/Unbekannt";

/** Nachgeladene Teile der Schale. Der Chat gehört B5b (screens/ki), das Blatt dieser Datei-
 *  gruppe; beide erst holen, wenn sie gebraucht werden. */
function ladeTeil<P>(laden: () => Promise<unknown>, name: string) {
  return lazy(async () => {
    const m = (await retryOnStaleShell(laden())) as Record<string, unknown>;
    const k = (m[name] ?? m.default) as ComponentType<P> | undefined;
    if (!k) throw new Error(`Baustein ${name} fehlt`);
    return { default: k };
  });
}
const ChatWidget = ladeTeil<{ V: string }>(() => import("../../screens/ki/ChatWidget"), "ChatWidget");
const VerfahrenBlatt = ladeTeil<{ alsBlatt?: boolean }>(() => import("../../screens/verfahren/VerfahrenBlatt"), "VerfahrenBlatt");

/** V aus dem Pfad: /v/:V/… direkt, Objektwege über das Code-Präfix (MU26-017-2c → MU26). */
export function verfahrenAusPfad(pathname: string): { V: string | null; ausV: boolean } {
  const m = /^\/v\/([^/]+)/.exec(pathname);
  if (m) {
    try {
      return { V: decodeURIComponent(m[1]!), ausV: true };
    } catch {
      return { V: m[1]!, ausV: true };
    }
  }
  const o = /^\/(d|b|m|x|a|q|r)\/([^/?#]+)/.exec(pathname);
  if (o) {
    let ref = o[2]!;
    try {
      ref = decodeURIComponent(ref);
    } catch {
      /* roh lassen */
    }
    return { V: verfahrenAusCode(ref), ausV: false };
  }
  return { V: null, ausV: false };
}

function LiveVerbindung({ V }: { V: string }) {
  useLive(V);
  return null;
}

export function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: ich } = useIch();
  const gemerkt = useStore((s) => s.verfahren);
  const setzeVerfahren = useStore((s) => s.setzeVerfahren);
  const blattOffen = useStore((s) => s.verfahrenBlatt);
  const setzeBlatt = useStore((s) => s.setzeVerfahrenBlatt);
  const dockBreite = useStore((s) => s.dockBreite);
  const [tastenOffen, setTastenOffen] = useState(false);

  const { V: pfadV, ausV } = useMemo(() => verfahrenAusPfad(pathname), [pathname]);
  const bekannt = (code: string | null) => !!code && !!ich?.verfahren.some((v) => v.code === code);

  // Ein :V ohne Zugriff → „Unbekannt“ (erst entscheiden, wenn `ich` da ist).
  const vUnbekannt = ausV && !!ich && !bekannt(pfadV);
  const V = bekannt(pfadV) ? pfadV : bekannt(gemerkt) ? gemerkt : null;

  const pfadBekannt = bekannt(pfadV);
  useEffect(() => {
    if (pfadV && pfadBekannt && pfadV !== gemerkt) setzeVerfahren(pfadV);
  }, [pfadV, pfadBekannt, gemerkt, setzeVerfahren]);

  // Noch nichts gewählt (z. B. direkt auf /admin/… gelandet) und es gibt GENAU ein Verfahren:
  // dieses vorwählen (landet als `hs.verfahren`), damit die Pille seinen Code zeigt statt „—“.
  const einziges = ich && !pfadBekannt ? einzigesVerfahren(ich.verfahren.map((v) => v.code), gemerkt) : null;
  useEffect(() => {
    if (einziges) setzeVerfahren(einziges);
  }, [einziges, setzeVerfahren]);

  // Sicherheitsnetz nach jedem Wegwechsel: ein übrig gebliebenes overflow:hidden am <body>
  // (Blatt/Dialog, deren Abbau nicht lief) wird gelöst, solange keine Ebene mehr offen ist.
  useEffect(() => {
    if (blattOffen) return;
    if (document.body.style.overflow === "hidden" && !document.querySelector(".modal-wrap, .ue-hof, .bottom-sheet")) document.body.style.overflow = "";
  }, [pathname, blattOffen]);

  // Esc: EIN Hörer, nur die oberste Ebene schließt (DESIGN §6.3 „topmost first“).
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (escOberste()) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const fragebar = (e: KeyboardEvent) => {
    e.preventDefault();
    if (fragebarFokussieren()) return;
    if (V) {
      navigate(`/v/${encodeURIComponent(V)}/auswerten`);
      fragebarSpaeterFokussieren();
    }
  };
  // F öffnet die Befehlssuche (Seiten, Werkzeuge, Codes, Text, KI fragen) — wie der Suchknopf;
  // Strg/⌘+K ebenso, auch aus einem Eingabefeld heraus, und schließt sie wieder. „/“ bleibt der
  // direkte Weg in die Fragebar. Bildschirme, die F oder „/“ selbst belegen (Prüfen, Kodieren), gehen vor.
  useTasten("global", {
    f: (e) => {
      e.preventDefault();
      useBefehlssuche.getState().oeffnen();
    },
    "!Mod+k": (e) => {
      e.preventDefault();
      useBefehlssuche.getState().umschalten();
    },
    "/": fragebar,
    "?": (e) => {
      e.preventDefault();
      setTastenOffen(true);
    },
  });

  const band = useBand(V);
  const bandH = band.length ? "26px" : "0px";
  const chatErlaubt = !!V && hatRecht(ich, V, "assistent");

  return (
    <div className="schale" style={{ display: "contents", "--band-h": bandH } as CSSProperties}>
      <TopNav V={V} ich={ich} />
      <Statusband saetze={band} />
      <div id="main-scroll" className="scroll" style={{ "--band-h": bandH, paddingRight: dockBreite || undefined } as CSSProperties}>
        <main className="blatt" id="inhalt">
          {vUnbekannt ? <Unbekannt /> : <Outlet />}
        </main>
        <Footer />
      </div>
      {V && <LiveVerbindung V={V} />}
      {chatErlaubt && V && (
        <ScreenBoundary still>
          <Suspense fallback={null}>
            <ChatWidget V={V} />
          </Suspense>
        </ScreenBoundary>
      )}
      <MobilLeiste V={V} />
      {blattOffen && (
        <ScreenBoundary still>
          <Suspense fallback={null}>
            <VerfahrenBlatt alsBlatt />
          </Suspense>
        </ScreenBoundary>
      )}
      <Befehlssuche V={V} ich={ich} tastenkuerzel={() => setTastenOffen(true)} />
      <Modal open={tastenOffen} onClose={() => setTastenOffen(false)} title="Tastenkürzel" icon="key" width={560}>
        <Tastenkuerzel />
      </Modal>
      {/* Das Blatt schließt beim Wegnavigieren (sonst stünde es über dem neuen Bildschirm). */}
      <BlattBeimWechselZu offen={blattOffen} zu={() => setzeBlatt(false)} pfad={pathname} />
    </div>
  );
}

function BlattBeimWechselZu({ offen, zu, pfad }: { offen: boolean; zu: () => void; pfad: string }) {
  const [start, setStart] = useState(pfad);
  useEffect(() => {
    if (!offen) {
      setStart(pfad);
      return;
    }
    if (pfad !== start) zu();
  }, [offen, pfad, start, zu]);
  return null;
}
